import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  apiFetch,
  fetchReviewQueueAccess,
  startSessionMonitoring,
  stopSessionMonitoring,
} from "../services/api";
import CookieNotice from "../components/CookieNotice";
import EmailVerification from "../components/EmailVerification";
import V2ResetPasswordPage from "./pages/V2ResetPasswordPage";
import V2ShellLayout from "./layout/V2ShellLayout";
import { useV2Navigation, V2_ROUTES, V2_ROUTE_ORDER } from "./state/useV2Navigation";
import V2HomePage from "./pages/V2HomePage";
import V2AddKnowledgePage from "./pages/V2AddKnowledgePage";
import V2InsightsInboxPage from "./pages/V2InsightsInboxPage";
import V2ArchitectureGalleryPage from "./pages/V2ArchitectureGalleryPage";
import V2ReportsPage from "./pages/V2ReportsPage";
import V2AdminPage from "./pages/V2AdminPage";
import V2ExplorerPage from "./pages/V2ExplorerPage";
import V2BaselineApprovalsPage from "./pages/V2BaselineApprovalsPage";
import V2SettingsPage from "./pages/V2SettingsPage";
import V2HelpPage from "./pages/V2HelpPage";
import V2LoginPage from "./pages/V2LoginPage";
import V2SignupPage from "./pages/V2SignupPage";
import V2EmailSentPage from "./pages/V2EmailSentPage";
import V2ForgotPasswordPage from "./pages/V2ForgotPasswordPage";
import V2GoogleCompleteSignupPage from "./pages/V2GoogleCompleteSignupPage";
import V2SeeItLivePage from "./pages/V2SeeItLivePage";
import { V2ThemeProvider } from "./theme/ThemeProvider";
import { V2EnrichmentReviewProvider } from "./state/V2EnrichmentReviewContext";
import { ProductGuideProvider } from "./guide/GuideContext";
import { MobileChromeProvider } from "./mobile/MobileChromeContext";
import DesktopOnlyGate from "./components/mobile/DesktopOnlyGate";
import { isDesktopOnlyRoute } from "./nav/navItems";
import { useDeviceDetection } from "../hooks/useDeviceDetection";
import {
  cleanAuthUrlParams,
  cleanStartupUrl,
  readAuthFlowFromUrl,
  readStartupNavigation,
} from "./utils/startupNavigation";
import {
  cleanOidcUrlParams,
  exchangeOidcCode,
  readOidcReturnFromUrl,
} from "./auth/oidcSso";
import { userCanContribute, userIsAdministrator } from "../utils/userRoles";
import {
  clearDemoSession,
  isDemoPath,
  isDemoSession,
} from "./demo/demoMode";
import { clearAgentChatClientState } from "../utils/agentChatClientState";

const MARKETING_URL = "https://decisionpen.com";

function isTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    if (!payload.exp) return true;
    return payload.exp <= currentTime;
  } catch {
    return true;
  }
}

function roleFromAccessToken(token) {
  try {
    const payload = JSON.parse(atob(String(token || "").split(".")[1] || ""));
    return String(payload?.role || "").trim();
  } catch {
    return "";
  }
}

/** Merge JWT claims into stored user; drop sticky demo flags. */
function hydrateSessionUser(rawUser, token) {
  const base =
    rawUser && typeof rawUser === "object" && !Array.isArray(rawUser)
      ? { ...rawUser }
      : {};
  delete base.is_demo;
  const jwtRole = roleFromAccessToken(token);
  if (jwtRole) {
    // Prefer the higher privilege so a fresh /me role upgrade is not
    // overwritten by an older Viewer JWT, and vice versa.
    const rank = { Viewer: 1, Editor: 2, Administrator: 3 };
    const stored = String(base.role || "").trim();
    const storedRank = rank[stored] || 0;
    const jwtRank = rank[jwtRole] || 0;
    base.role = jwtRank >= storedRank ? jwtRole : stored || jwtRole;
  }
  try {
    const payload = JSON.parse(atob(String(token || "").split(".")[1] || ""));
    if (!base.id && payload?.sub) base.id = payload.sub;
    if (!base.email && payload?.email) base.email = payload.email;
    if (!base.organization_id && payload?.organization_id) {
      base.organization_id = payload.organization_id;
    }
  } catch {
    /* ignore */
  }
  return Object.keys(base).length ? base : null;
}

function readSession() {
  try {
    const token = localStorage.getItem("access_token");
    if (!token || isTokenExpired(token)) {
      if (token) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
      }
      return null;
    }
    const raw = JSON.parse(localStorage.getItem("user") || "null");
    const user = hydrateSessionUser(raw, token);
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    }
    return { token, user };
  } catch {
    return null;
  }
}

function bootstrapAuthState() {
  const token = localStorage.getItem("access_token");
  if (!token) return { session: null, notice: "" };
  if (isTokenExpired(token)) {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    return {
      session: null,
      notice: "Your session has expired. Please log in again.",
    };
  }
  try {
    const session = readSession();
    return { session, notice: "" };
  } catch {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    return { session: null, notice: "" };
  }
}

const AppV2Shell = () => {
  const { isMobile } = useDeviceDetection();
  const initialAuthRef = useRef(null);
  if (initialAuthRef.current === null) {
    initialAuthRef.current = bootstrapAuthState();
  }
  const startupNavRef = useRef(readStartupNavigation());
  const authFlowRef = useRef(readAuthFlowFromUrl());

  const initialRoute =
    startupNavRef.current?.route && !authFlowRef.current
      ? startupNavRef.current.route
      : V2_ROUTES.home;

  const [session, setSession] = useState(initialAuthRef.current.session);
  // /demo: marketing signup entry into a normal org (shared demo org retired).
  // Sample/guided demo is offered in onboarding for new users, not here.
  const liveEntryRef = useRef(isDemoPath());
  const [authScreen, setAuthScreen] = useState("login");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [pendingGoogleSignup, setPendingGoogleSignup] = useState(null);
  const [sessionNotice, setSessionNotice] = useState(initialAuthRef.current.notice);
  const [explorerLaunch, setExplorerLaunch] = useState(
    () => startupNavRef.current?.explorerProps || null
  );
  const [canAccessApprovals, setCanAccessApprovals] = useState(false);
  const [magicState, setMagicState] = useState("idle");
  // True while OIDC/code exchange is in flight — avoid flashing the login form.
  const [authHandoffPending, setAuthHandoffPending] = useState(() => {
    try {
      if (
        sessionStorage.getItem("pending_oidc_code") ||
        sessionStorage.getItem("pending_oidc_token") ||
        sessionStorage.getItem("oidc_exchange_lock") === "1"
      ) {
        return true;
      }
    } catch {
      /* ignore */
    }
    const oidc = readOidcReturnFromUrl();
    return oidc?.type === "oidc-code" || oidc?.type === "oidc-success";
  });

  // Company OIDC SSO return: ?oidc_code= → exchange → session.
  // Persist handoff in sessionStorage and single-flight so React Strict Mode
  // remounts do not double-consume the one-time code or discard a success.
  useEffect(() => {
    const oidc = readOidcReturnFromUrl();
    if (oidc?.type === "oidc-error") {
      cleanOidcUrlParams();
      sessionStorage.removeItem("pending_oidc_code");
      sessionStorage.removeItem("pending_oidc_token");
      sessionStorage.removeItem("oidc_exchange_lock");
      setAuthHandoffPending(false);
      setSessionNotice(oidc.message || "Company SSO failed.");
      setAuthScreen("login");
      return undefined;
    }
    if (oidc?.type === "oidc-code" && oidc.code) {
      sessionStorage.setItem("pending_oidc_code", oidc.code);
      cleanOidcUrlParams();
      setAuthHandoffPending(true);
    } else if (oidc?.type === "oidc-success" && oidc.token) {
      sessionStorage.setItem("pending_oidc_token", oidc.token);
      cleanOidcUrlParams();
      setAuthHandoffPending(true);
    }

    const pendingCode = sessionStorage.getItem("pending_oidc_code");
    const pendingToken = sessionStorage.getItem("pending_oidc_token");
    if (!pendingCode && !pendingToken) return undefined;
    if (sessionStorage.getItem("oidc_exchange_lock") === "1") return undefined;
    sessionStorage.setItem("oidc_exchange_lock", "1");
    setAuthHandoffPending(true);

    (async () => {
      try {
        let accessToken = null;
        let userPayload = null;
        if (pendingCode) {
          const data = await exchangeOidcCode(pendingCode);
          accessToken = data.access_token;
          userPayload = data.user || null;
        } else if (pendingToken) {
          accessToken = pendingToken;
        }
        if (!accessToken) throw new Error("no token");
        localStorage.setItem("access_token", accessToken);
        if (userPayload) {
          localStorage.setItem("user", JSON.stringify(userPayload));
        } else {
          const res = await apiFetch("/api/auth/me");
          if (!res.ok) throw new Error("me failed");
          localStorage.setItem("user", JSON.stringify(await res.json()));
        }
        sessionStorage.removeItem("pending_oidc_code");
        sessionStorage.removeItem("pending_oidc_token");
        sessionStorage.removeItem("oidc_exchange_lock");
        await finalizeAuthenticatedSession();
        setAuthHandoffPending(false);
      } catch {
        sessionStorage.removeItem("pending_oidc_code");
        sessionStorage.removeItem("pending_oidc_token");
        sessionStorage.removeItem("oidc_exchange_lock");
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
        setAuthHandoffPending(false);
        setSessionNotice(
          "Company SSO completed but session setup failed. Please try again."
        );
        setAuthScreen("login");
      }
    })();
    return undefined;
  }, []);

  // Magic-link return: redeem token -> JWT -> agent Home.
  useEffect(() => {
    const flow = authFlowRef.current;
    if (!flow || flow.type !== "magic-link") return undefined;
    let cancelled = false;
    setMagicState("loading");
    (async () => {
      try {
        const res = await apiFetch("/api/auth/magic-link/verify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: flow.email, token: flow.token }),
        });
        if (!res.ok) throw new Error("magic failed");
        const data = await res.json();
        if (cancelled) return;
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("user", JSON.stringify(data.user));
        cleanAuthUrlParams();
        authFlowRef.current = null;
        setMagicState("done");
        await finalizeAuthenticatedSession();
      } catch {
        if (!cancelled) setMagicState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Legacy shared-demo sessions can no longer be created; clear any
  // stored one so returning visitors land on the gate, not a frozen org.
  useEffect(() => {
    if (!liveEntryRef.current) return;
    if (isDemoSession()) {
      clearDemoSession();
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      setSession(null);
    }
  }, []);

  // Existing sessions: strip sticky is_demo and refresh role from /me once.
  useEffect(() => {
    if (!session?.token || authFlowRef.current) return undefined;
    let cancelled = false;
    (async () => {
      clearDemoSession();
      try {
        const meRes = await apiFetch("/api/auth/me");
        if (!meRes.ok || cancelled) return;
        const me = await meRes.json();
        if (!me || typeof me !== "object" || cancelled) return;
        const { is_demo: _drop, ...safe } = me;
        localStorage.setItem("user", JSON.stringify(safe));
        if (!cancelled) setSession(readSession());
      } catch {
        /* keep bootstrap session */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const shellRoutes = useMemo(() => {
    let routes = V2_ROUTE_ORDER;
    const canAdmin = userIsAdministrator(session?.user);
    const canContribute = userCanContribute(session?.user);
    // Ephemeral /demo is read-only only for non-contributor sessions.
    // Never strip Admin/Editor nav because of a sticky demo flag.
    if (isDemoSession() && !canContribute) {
      routes = routes.filter(
        (item) => item !== V2_ROUTES.admin && item !== V2_ROUTES.addKnowledge
      );
    }
    if (!canAdmin) {
      routes = routes.filter((item) => item !== V2_ROUTES.admin);
    }
    // Viewers are read-only: no Add knowledge / Inbox (Editors+ can contribute).
    if (!canContribute) {
      routes = routes.filter(
        (item) =>
          item !== V2_ROUTES.addKnowledge && item !== V2_ROUTES.insightsInbox
      );
    }
    if (!canAccessApprovals) {
      routes = routes.filter((item) => item !== V2_ROUTES.baselineApprovals);
    }
    return routes;
  }, [session?.user, canAccessApprovals]);

  const { route, routes, navigate } = useV2Navigation(initialRoute, shellRoutes);

  useEffect(() => {
    const openEntity = (event) => {
      const entityId = String(event?.detail?.entityId || "").trim();
      if (!entityId) return;
      setExplorerLaunch({ initialEntityId: entityId });
      navigate(V2_ROUTES.explorer);
    };
    window.addEventListener("v2-open-entity", openEntity);
    return () => window.removeEventListener("v2-open-entity", openEntity);
  }, [navigate]);

  useEffect(() => {
    if (!session?.user) return;
    if (route === V2_ROUTES.admin && !userIsAdministrator(session.user)) {
      navigate(V2_ROUTES.home);
    }
    if (route === V2_ROUTES.baselineApprovals && !canAccessApprovals) {
      navigate(V2_ROUTES.home);
    }
    if (route === V2_ROUTES.insightsInbox && !userCanContribute(session.user)) {
      navigate(V2_ROUTES.home);
    }
    if (route === V2_ROUTES.addKnowledge && !userCanContribute(session.user)) {
      navigate(V2_ROUTES.home);
    }
  }, [route, session?.user, canAccessApprovals, navigate]);

  useEffect(() => {
    if (!session?.user) {
      setCanAccessApprovals(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const access = await fetchReviewQueueAccess();
        if (!cancelled) {
          setCanAccessApprovals(Boolean(access?.in_approver_group));
        }
      } catch {
        if (!cancelled) setCanAccessApprovals(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.user]);

  useEffect(() => {
    if (!session?.token || authFlowRef.current) return;
    if (startupNavRef.current) {
      cleanStartupUrl();
      startupNavRef.current = null;
    }
  }, [session?.token]);

  const clearAuthenticatedSession = useCallback((notice = "") => {
    stopSessionMonitoring();
    clearDemoSession();
    clearAgentChatClientState({ emitEvent: false });
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    localStorage.removeItem("trialStatus");
    setSession(null);
    setAuthScreen("login");
    if (notice) setSessionNotice(notice);
  }, []);

  useEffect(() => {
    if (session?.token) {
      startSessionMonitoring();
      return () => stopSessionMonitoring();
    }
    stopSessionMonitoring();
    return undefined;
  }, [session?.token]);

  useEffect(() => {
    const handleForceLogout = (event) => {
      const isManualLogout =
        localStorage.getItem("manual_logout") === "true" ||
        localStorage.getItem("last_logout_reason") === "manual";
      const reason = event?.detail?.reason || "Session expired";
      const signedOutExplicitly =
        reason === "Signed out" || reason === "manual";

      stopSessionMonitoring();

      if (!isManualLogout && !signedOutExplicitly) {
        const notice =
          reason === "Invalid token"
            ? "Your session is invalid. Please log in again."
            : "Your session has expired. Please log in again.";
        clearAuthenticatedSession(notice);
        return;
      }

      clearAuthenticatedSession("");
    };

    window.addEventListener("forceLogout", handleForceLogout);
    return () => window.removeEventListener("forceLogout", handleForceLogout);
  }, [clearAuthenticatedSession]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === "access_token" && !event.newValue) {
        clearAuthenticatedSession(
          "Your session has ended. Please log in again."
        );
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [clearAuthenticatedSession]);

  const finalizeAuthenticatedSession = useCallback(async () => {
    clearDemoSession();
    // New login / signup: never resume another account's Ask thread.
    clearAgentChatClientState({ emitEvent: false });
    localStorage.removeItem("last_logout_reason");
    localStorage.removeItem("manual_logout");
    setSessionNotice("");
    setAuthHandoffPending(false);
    // Enter the workspace immediately — login/SSO already wrote token+user.
    // Waiting on /me first left the sign-in form on screen for 1–2s.
    const immediate = readSession();
    if (immediate?.token) {
      setSession(immediate);
      startSessionMonitoring();
    }
    // Hydrate canonical role/profile in the background.
    try {
      const meRes = await apiFetch("/api/auth/me");
      if (meRes.ok) {
        const me = await meRes.json();
        if (me && typeof me === "object") {
          const { is_demo: _drop, ...safe } = me;
          localStorage.setItem("user", JSON.stringify(safe));
          setSession(readSession());
        }
      }
    } catch {
      /* keep whatever login already stored */
    }
    try {
      const trialResponse = await apiFetch("/api/auth/trial-status");
      if (trialResponse.ok) {
        const trialData = await trialResponse.json();
        localStorage.setItem("trialStatus", JSON.stringify(trialData));
        window.dispatchEvent(new CustomEvent("trialStatusUpdated", { detail: trialData }));
      }
    } catch {
      /* ignore */
    }
  }, []);

  const handleSignOut = useCallback(() => {
    localStorage.setItem("last_logout_reason", "manual");
    localStorage.setItem("manual_logout", "true");
    stopSessionMonitoring();
    clearAgentChatClientState({ emitEvent: false });
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    localStorage.removeItem("trialStatus");
    clearAuthenticatedSession("");
  }, [clearAuthenticatedSession]);

  const handleGateAuthenticated = useCallback(() => {
    clearDemoSession();
    liveEntryRef.current = false;
    void finalizeAuthenticatedSession();
  }, [finalizeAuthenticatedSession]);

  const handleGateNeedsGoogle = useCallback((pending) => {
    clearDemoSession();
    clearAgentChatClientState({ emitEvent: false });
    liveEntryRef.current = false;
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setSession(null);
    setPendingGoogleSignup(pending);
    setAuthScreen("google-complete");
  }, []);

  const handleGateFullSignup = useCallback(() => {
    clearDemoSession();
    clearAgentChatClientState({ emitEvent: false });
    liveEntryRef.current = false;
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setSession(null);
    setAuthScreen("signup");
  }, []);

  const handleGateSignIn = useCallback(() => {
    clearDemoSession();
    clearAgentChatClientState({ emitEvent: false });
    liveEntryRef.current = false;
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setSession(null);
    setAuthScreen("login");
  }, []);

  const handleGateEmailSent = useCallback((email, sent) => {
    clearDemoSession();
    clearAgentChatClientState({ emitEvent: false });
    liveEntryRef.current = false;
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setSession(null);
    setVerificationEmail(email || "");
    setVerificationEmailSent(Boolean(sent));
    setAuthScreen("email-sent");
  }, []);

  // Keep Ask (Home) mounted across menu switches so follow-up chips / thread
  // state are not wiped by remount. Hide it when another route is active.
  const homeActive =
    route === V2_ROUTES.home &&
    !(isMobile && isDesktopOnlyRoute(route));

  const renderNonHomePage = () => {
    if (isMobile && isDesktopOnlyRoute(route)) {
      return <DesktopOnlyGate routeKey={route} onNavigate={navigate} />;
    }
    if (route === V2_ROUTES.insightsInbox) return <V2InsightsInboxPage onNavigate={navigate} />;
    if (route === V2_ROUTES.baselineApprovals) return <V2BaselineApprovalsPage />;
    if (route === V2_ROUTES.reports) return <V2ReportsPage />;
    if (route === V2_ROUTES.explorer) {
      return (
        <V2ExplorerPage
          {...(explorerLaunch || {})}
          key={
            explorerLaunch?.initialEntityId
              ? `entity-${explorerLaunch.initialEntityId}`
              : "explorer-default"
          }
        />
      );
    }
    if (route === V2_ROUTES.admin) return <V2AdminPage />;
    if (route === V2_ROUTES.help) return <V2HelpPage onNavigate={navigate} />;
    if (route === V2_ROUTES.settings) return <V2SettingsPage />;
    if (route === V2_ROUTES.architectureGallery) {
      return <V2ArchitectureGalleryPage onNavigate={navigate} />;
    }
    if (route === V2_ROUTES.addKnowledge) return <V2AddKnowledgePage onNavigate={navigate} />;
    return null;
  };

  if (!session?.token) {
    const authFlow = authFlowRef.current;

    const signingInSplash = (message = "Signing you in…") => (
      <V2ThemeProvider>
        <div className="flex min-h-screen items-center justify-center px-4 bg-slate-50">
          <div className="text-center">
            <p className="text-sm font-medium text-slate-700">{message}</p>
            <p className="mt-1 text-xs text-slate-500">Loading your workspace</p>
          </div>
        </div>
        <CookieNotice />
      </V2ThemeProvider>
    );

    // Company SSO return / in-flight exchange — never flash the empty login form.
    if (authHandoffPending) {
      return signingInSplash("Completing company sign-in…");
    }

    if (authFlow?.type === "magic-link") {
      if (magicState === "error") {
        return (
          <V2ThemeProvider>
            <div className="flex min-h-screen items-center justify-center px-4">
              <div className="max-w-sm text-center">
                <h1 className="text-lg font-semibold text-slate-900">Sign-in link expired</h1>
                <p className="mt-2 text-sm text-slate-600">
                  This link is invalid or has expired. Request a new one or sign in below.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    cleanAuthUrlParams();
                    authFlowRef.current = null;
                    setMagicState("idle");
                    setAuthScreen("login");
                  }}
                  className="mt-4 rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white"
                >
                  Go to sign in
                </button>
              </div>
            </div>
            <CookieNotice />
          </V2ThemeProvider>
        );
      }
      return signingInSplash();
    }

    if (authFlow?.type === "reset-password") {
      return (
        <V2ThemeProvider>
          <V2ResetPasswordPage
            email={authFlow.email}
            token={authFlow.token}
            isInvitation={Boolean(authFlow.isInvitation)}
            onSuccess={() => {
              cleanAuthUrlParams();
              authFlowRef.current = null;
              setAuthScreen("login");
              setSessionNotice(
                authFlow.isInvitation
                  ? "Account ready. Sign in with your new password."
                  : "Password updated. You can sign in now."
              );
            }}
          />
          <CookieNotice />
        </V2ThemeProvider>
      );
    }

    if (liveEntryRef.current) {
      // Marketing “See it live” signup — normal workspace; sample path via onboarding.
      return (
        <V2ThemeProvider>
          <V2SeeItLivePage
            onAuthenticated={handleGateAuthenticated}
            onNeedsGoogleSignup={handleGateNeedsGoogle}
            onEmailSent={handleGateEmailSent}
            onSignIn={handleGateSignIn}
            onBackToWebsite={() => window.location.assign(MARKETING_URL)}
          />
          <CookieNotice />
        </V2ThemeProvider>
      );
    }

    if (authFlow?.type === "email-verify") {
      return (
        <V2ThemeProvider>
          <EmailVerification
            email={authFlow.email}
            token={authFlow.token}
            onVerificationComplete={() => {
              cleanAuthUrlParams();
              authFlowRef.current = null;
              setAuthScreen("login");
              setSessionNotice("Email verified. You can sign in now.");
            }}
          />
          <CookieNotice />
        </V2ThemeProvider>
      );
    }

    return (
      <V2ThemeProvider>
        {authScreen === "google-complete" ? (
          <V2GoogleCompleteSignupPage
            pendingGoogle={pendingGoogleSignup}
            onSuccess={finalizeAuthenticatedSession}
            onCancel={() => {
              setPendingGoogleSignup(null);
              setAuthScreen("login");
            }}
          />
        ) : authScreen === "signup" ? (
          <V2SignupPage
            onLogin={() => setAuthScreen("login")}
            onEmailSent={(email, sent) => {
              setVerificationEmail(email || "");
              setVerificationEmailSent(Boolean(sent));
              setAuthScreen("email-sent");
            }}
            onSignupAuthenticated={finalizeAuthenticatedSession}
            onNeedsGoogleSignup={(pending) => {
              setPendingGoogleSignup(pending);
              setAuthScreen("google-complete");
            }}
          />
        ) : authScreen === "email-sent" ? (
          <V2EmailSentPage
            email={verificationEmail}
            initialEmailSent={verificationEmailSent}
            onBackToLogin={() => setAuthScreen("login")}
          />
        ) : authScreen === "forgot-password" ? (
          <V2ForgotPasswordPage onBackToLogin={() => setAuthScreen("login")} />
        ) : (
          <V2LoginPage
            sessionNotice={sessionNotice}
            onLoginSuccess={finalizeAuthenticatedSession}
            onSignup={() => {
              // New accounts use /demo marketing entry (not a third signup page).
              window.location.assign("/demo");
            }}
            onForgotPassword={() => setAuthScreen("forgot-password")}
            onNeedsGoogleSignup={(pending) => {
              setPendingGoogleSignup(pending);
              setAuthScreen("google-complete");
            }}
          />
        )}
        <CookieNotice />
      </V2ThemeProvider>
    );
  }

  return (
    <V2ThemeProvider>
      <V2EnrichmentReviewProvider>
        <MobileChromeProvider>
          <ProductGuideProvider
            currentRoute={route}
            onNavigate={navigate}
            onOpenHomeAdd={() => {
              try {
                sessionStorage.setItem("dp_open_add_panel", "1");
              } catch {
                /* ignore */
              }
              navigate(V2_ROUTES.addKnowledge);
            }}
            onOpenHomeAsk={() => {
              try {
                sessionStorage.setItem("dp_open_ask_panel", "1");
              } catch {
                /* ignore */
              }
              navigate(V2_ROUTES.home);
            }}
          >
            <V2ShellLayout
              route={route}
              routes={routes}
              onNavigate={navigate}
              onSignOut={handleSignOut}
            >
              <div
                className={
                  homeActive
                    ? "flex min-h-0 flex-1 flex-col"
                    : "hidden"
                }
                aria-hidden={!homeActive}
              >
                <V2HomePage
                  onNavigate={navigate}
                  route={route}
                  isActive={homeActive}
                  onGateAuthenticated={handleGateAuthenticated}
                  onGateNeedsGoogle={handleGateNeedsGoogle}
                  onGateFullSignup={handleGateFullSignup}
                />
              </div>
              {!homeActive ? renderNonHomePage() : null}
            </V2ShellLayout>
            {/* Agent script is the onboarding path; video modal demoted. */}
            <CookieNotice />
          </ProductGuideProvider>
        </MobileChromeProvider>
      </V2EnrichmentReviewProvider>
    </V2ThemeProvider>
  );
};

export default AppV2Shell;
